import { createContext, useContext, useState, useMemo, useLayoutEffect } from 'react';
import { isMobileApp } from '../../utils/getHost';

export const MOBILE_MAX_WIDTH = 700;
const RESIZE_DEBOUNCE_MS = 100;

const MobileContext = createContext(undefined);

const debounce = (fn, delay) => {
    let timeoutId;
    const debounced = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
    debounced.cancel = () => clearTimeout(timeoutId);
    return debounced;
};

/**
 * Provider component that manages mobile layout detection
 */
const MobileLayoutProvider = ({ children }) => {
    const [mobileView, setMobileView] = useState(() => 
        typeof window !== 'undefined' ? window.innerWidth < MOBILE_MAX_WIDTH : false
    );

    useLayoutEffect(() => {
        const update = debounce(() => {
            setMobileView(window.innerWidth < MOBILE_MAX_WIDTH);
        }, RESIZE_DEBOUNCE_MS);

        window.addEventListener('resize', update, { passive: true });
        
        return () => {
            update.cancel();
            window.removeEventListener('resize', update);
        };
    }, []);

    const value = useMemo(
        () => ({
            mobileView,
            isMobileLayout: isMobileApp || mobileView
        }),
        [isMobileApp, mobileView]
    );

    return (
        <MobileContext.Provider value={value}>
            {children}
        </MobileContext.Provider>
    );
};

export default MobileLayoutProvider;

export const useMobileLayout = () => {
    const context = useContext(MobileContext);
    
    if (context === undefined) {
        throw new Error('useMobileLayout must be used within MobileLayoutProvider');
    }
    
    return context;
};